"use client";

import {
  dotToStateValue,
  getArcStatesForJump,
} from "@/app/directories/kiosk/machine/helper/arc-helpers";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { AnyStateMachine } from "xstate";
import { traceInspector } from "../../trace-inspector/trace-inspector";

interface JumpTabProps {
  states: Record<
    string,
    {
      componentName: string | null;
      contentKey: string | null;
      type: "final" | "normal";
      transitions: never[];
    }
  >;
  currentState: string;
  onJump: (
    stateName: string,
    arc?: { arcId: string; stateName: string | Record<string, unknown> },
  ) => void;
  machine?: AnyStateMachine;
}

export function JumpTab({
  states,
  currentState,
  onJump,
  machine,
}: JumpTabProps) {
  const [expandedArcs, setExpandedArcs] = useState<Set<string>>(new Set());

  // Derive arc state info from the machine definition
  const arcInfo = useMemo(() => {
    if (!machine) return {};
    return getArcStatesForJump(machine);
  }, [machine]);

  // Subscribe to arc child context so the "Current" badge updates during arc navigation
  const [childSnapshot, setChildSnapshot] = useState(() =>
    traceInspector.getChildSnapshot(),
  );
  useEffect(() => {
    return traceInspector.subscribeToChildContext(() => {
      setChildSnapshot(traceInspector.getChildSnapshot());
    });
  }, []);

  const toggleArc = (stateName: string) => {
    setExpandedArcs((prev) => {
      const next = new Set(prev);
      if (next.has(stateName)) {
        next.delete(stateName);
      } else {
        next.add(stateName);
      }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Jump to State
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Click any state to jump directly to it (preserves current context).
          States with arcs can be expanded to jump to arc-internal states.
        </p>
      </div>

      {/* State List */}
      <div className="space-y-1 overflow-y-auto">
        {Object.entries(states).map(([stateName]) => {
          const isCurrent = stateName === currentState;
          const arc = arcInfo[stateName];
          const isExpanded = expandedArcs.has(stateName);

          return (
            <div key={stateName}>
              {/* Parent state button */}
              <div className="flex items-center gap-1">
                {arc ? (
                  <button
                    type="button"
                    onClick={() => toggleArc(stateName)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    title={
                      isExpanded ? "Collapse arc states" : "Expand arc states"
                    }
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </button>
                ) : (
                  <div className="w-[26px]" />
                )}
                <button
                  type="button"
                  onClick={() => onJump(stateName)}
                  disabled={isCurrent}
                  className={`flex-1 text-left p-2.5 rounded-lg border transition-colors ${
                    isCurrent
                      ? "bg-blue-50 border-blue-300 cursor-not-allowed"
                      : "bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-gray-900">
                        {stateName}
                      </span>
                      {arc && (
                        <span className="text-xs text-gray-400 font-normal">
                          ({arc.states.length} arc states)
                        </span>
                      )}
                    </div>
                    {isCurrent && (
                      <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                        Current
                      </span>
                    )}
                  </div>
                </button>
              </div>

              {/* Arc internal states (expandable) */}
              {arc && isExpanded && (
                <div className="ml-[26px] pl-3 border-l-2 border-gray-200 mt-1 mb-2 space-y-1">
                  {arc.states.map((arcStateName) => {
                    const arcStateValue = dotToStateValue(arcStateName);
                    const isCurrentArcState =
                      isCurrent &&
                      childSnapshot?.arcId === arc.arcId &&
                      JSON.stringify(childSnapshot?.stateName) ===
                        JSON.stringify(arcStateValue);

                    return (
                      <button
                        key={arcStateName}
                        type="button"
                        onClick={() =>
                          onJump(stateName, {
                            arcId: arc.arcId,
                            stateName: arcStateName,
                          })
                        }
                        disabled={isCurrentArcState}
                        className={`w-full text-left p-2 rounded-md border transition-colors ${
                          isCurrentArcState
                            ? "bg-purple-50 border-purple-300 cursor-not-allowed"
                            : "bg-gray-50 border-gray-100 hover:border-purple-300 hover:bg-purple-50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-gray-700">
                            {arcStateName}
                          </span>
                          {isCurrentArcState && (
                            <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                              Current
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
